import { useCallback, useSyncExternalStore } from 'react'

/**
 * The one place JavaScript is allowed to ask how wide the window is.
 *
 * 🔴 CSS ANSWERS THIS QUESTION EVERYWHERE IT CAN, AND THAT IS NOT A STYLE
 * PREFERENCE — IT IS WHAT KEEPS THE TWO ANSWERS FROM DISAGREEING. A `md:hidden`
 * and a `useMediaQuery('(min-width: 48rem)')` resolve at different moments: the
 * class is applied by the paint, the hook by an effect after it. A shell that
 * decided its layout in JS would render the desktop shape for one frame on
 * every phone load, and any component that mixed the two would draw a sidebar
 * and a thumb bar together while they caught up.
 *
 * So the rule this file exists to enforce is: **CSS decides what is SHOWN, this
 * decides what BEHAVES.** A drawer's visibility is `lg:hidden`. Whether the
 * Escape key closes it, whether focus is trapped inside it, and whether the
 * page underneath is inert are behaviours, and no class can express them.
 *
 * ⚠️ `useSyncExternalStore` RATHER THAN `useState` + `useEffect`, AND THE
 * DIFFERENCE IS THE FIRST PAINT. The effect version starts at a guessed value
 * and corrects itself one commit later, which is the flash described above. This
 * reads the real value during render, synchronously, on the very first one.
 *
 * ⚠️ THE QUERY MUST BE A CONSTANT. It is the subscription key; an inline
 * template string that changes every render resubscribes every render. Every
 * caller in this product passes one of the exported constants below.
 */

/**
 * The breakpoints, named, so a component never types a number.
 *
 * 🔴 THEY MATCH TAILWIND v4's DEFAULTS EXACTLY, AND THEY HAVE TO. Every layout
 * decision in the product is a `md:` or `lg:` class; if this file said `47rem`
 * the behaviour would switch one pixel before the layout did, and the drawer
 * would be trapping focus in a sidebar that is already on screen.
 */
export const BP = {
  /** 40rem / 640px — Tailwind `sm`. A large phone in landscape. */
  sm: '(min-width: 40rem)',
  /** 48rem / 768px — Tailwind `md`. A tablet in portrait; the parent tier's sidebar. */
  md: '(min-width: 48rem)',
  /** 64rem / 1024px — Tailwind `lg`. The admin tiers' sidebar; a laptop. */
  lg: '(min-width: 64rem)',
  /** 80rem / 1280px — Tailwind `xl`. */
  xl: '(min-width: 80rem)',
  /**
   * A pointer that cannot hover precisely — a finger.
   *
   * ⚠️ NOT THE SAME QUESTION AS "IS IT NARROW". A 12" tablet is 1024px wide and
   * still a touch device, and a laptop dragged to 500px is still a mouse. Size
   * decides layout; this decides whether a hover-only affordance is the only way
   * to reach something.
   */
  coarse: '(pointer: coarse)',
} as const

const NOOP = () => () => {}

/**
 * `true` while the query matches. Re-renders on change.
 *
 * Returns `false` where `matchMedia` does not exist — jsdom under Vitest, and
 * any server render. ⚠️ THAT DEFAULT IS DELIBERATELY THE NARROW ONE: every query
 * here is `min-width`, so `false` means "phone", and a phone layout rendered on
 * a desktop for one frame is a column that is too narrow. The reverse — a
 * desktop layout rendered on a phone — is a sidebar covering the screen.
 */
export function useMediaQuery(query: string): boolean {
  const subscribe = useCallback(
    (onChange: () => void) => {
      if (typeof matchMedia !== 'function') return NOOP()
      const mq = matchMedia(query)
      mq.addEventListener('change', onChange)
      return () => mq.removeEventListener('change', onChange)
    },
    [query],
  )

  const get = useCallback(
    () => (typeof matchMedia === 'function' ? matchMedia(query).matches : false),
    [query],
  )

  /* The third argument is the server snapshot — same reasoning as `get`'s fallback. */
  return useSyncExternalStore(subscribe, get, () => false)
}

/**
 * The three device classes the product is designed for, as one word.
 *
 * ⚠️ USE THIS ONLY WHEN THE BEHAVIOUR GENUINELY FORKS THREE WAYS. Most callers
 * want one boolean — "is the sidebar a drawer right now" — and a single
 * `useMediaQuery(BP.lg)` says that more precisely than a string comparison does.
 */
export type Device = 'phone' | 'tablet' | 'desktop'

export function useDevice(): Device {
  const md = useMediaQuery(BP.md)
  const lg = useMediaQuery(BP.lg)
  if (lg) return 'desktop'
  if (md) return 'tablet'
  return 'phone'
}
